import React, { useState, useEffect, useContext } from 'react';
import Helmet from "react-helmet";
import { Link, NavLink } from "react-router-dom";

import ClusterApi from "../../api/ClusterApi";

// Types
import Cluster from "../../types/cluster/Cluster";
import ClustersDataTable from "../../types/cluster/tables/ClustersDataTable";

// Components
import PeriodBreadcrumb from "../../component/PeriodBreadcrumb";
import HeaderActions from "../../container/header_actions";
import WidgetCard from "../../component/instance/widget";
import Breadcrumb from "../../container/breadcrumb";
import ClustersTable from "../../component/cluster/table/ClustersTable";
import ConditionalRender from "../../helpers/ConditionalRender";

// Constants
import { DATA_LOADED, DATA_LOADING } from "../../component/Constants";

// Contexts
import { TimeRangeContext } from "../../context/TimeRangeContext";

interface IListProps {
    toggleMenu: Function,
}

const List: React.FC<IListProps> = (props) => {
    const [loading, setLoading] = useState<number>(DATA_LOADING)
    const [refreshCounter, setRefreshCounter] = useState<number>(0)
    const timeRangeContext = useContext(TimeRangeContext);

    const [clustersDataTable, setClustersDataTable] = useState<ClustersDataTable[]>([])
    const [clusterCount, setClusterCount] = useState<number>(0)
    const [groupSummary, setGroupSummary] = useState<React.ReactNode>('')
    const [replicaSummary, setReplicaSummary] = useState<React.ReactNode>('')
    const [alertCount, setAlertCount] = useState<number>(0)

    useEffect(() => {
        let active = true;

        async function load() {
            setLoading(DATA_LOADING);

            const clusters: Cluster[] = await ClusterApi.getClusters();
            const [groups, listeners, nodesPerCluster, eventsPerCluster] = await Promise.all([
                ClusterApi.getAvailabilityGroups(),
                ClusterApi.getListeners(),
                Promise.all(clusters.map(cluster => ClusterApi.getNodes(cluster.id))),
                Promise.all(clusters.map(cluster =>
                    ClusterApi.getEvents(cluster.id, timeRangeContext.timeRange))),
            ]);
            if (!active) return;

            const replicaCounts = await Promise.all(groups.map(group => ClusterApi.getReplicas(group.id)));
            if (!active) return;

            const allReplicas = replicaCounts.flat();
            const healthyGroups = groups.filter(group => group.health === 'HEALTHY').length;
            const healthyReplicas = allReplicas.filter(replica => replica.synchronizationhealth === 'HEALTHY').length;

            setClusterCount(clusters.length);
            setGroupSummary(<span>{healthyGroups} of {groups.length} healthy</span>);
            setReplicaSummary(<span>{healthyReplicas} of {allReplicas.length} healthy</span>);

            const tableData: ClustersDataTable[] = clusters.map((cluster, index) => {
                const clusterGroups = groups.filter(group => group.clusterid === cluster.id);
                const clusterListeners = listeners.filter(listener =>
                    clusterGroups.some(group => group.id === listener.groupid));
                const offline = clusterListeners.filter(listener => listener.state !== 'ONLINE').length;
                const alerts = eventsPerCluster[index].filter(event => event.event === 'alert');
                const raised = alerts.filter(alert => alert.severity > 0);
                return {
                    id: cluster.id,
                    name: cluster.name,
                    type: cluster.type,
                    health: cluster.health,
                    availabilitygroups: clusterGroups.length,
                    nodes: nodesPerCluster[index].length,
                    listeners: offline > 0
                        ? `${offline} of ${clusterListeners.length} offline`
                        : `${clusterListeners.length} online`,
                    alerts: alerts.length,
                    // Severity 0 marks a recovery rather than a severity, so
                    // folding it into min() painted the count green as soon as
                    // anything had recovered in the period, however critical
                    // the alerts beside it still were.
                    worstseverity: raised.length
                        ? Math.min(...raised.map(alert => alert.severity)) : 0,
                };
            });

            setClustersDataTable(tableData);
            setAlertCount(tableData.reduce((total, row) => total + row.alerts, 0));
            setLoading(DATA_LOADED);
        }

        void load();
        return () => {
            active = false;
        };
    }, [refreshCounter, timeRangeContext]);

    const refresh = () => {
        setLoading(DATA_LOADING)
        setClustersDataTable([])
        setRefreshCounter(counter => counter + 1)
    }

    return (
        <div id="content">
            <Helmet>
                <title>Clusters - DBmarlin</title>
                <meta name="description" content="List of all monitored clusters with availability group health"/>
            </Helmet>

            {/* Heading */}
            <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                <Breadcrumb heading="Clusters">
                    <Link to="/clusters">Analysis</Link>
                    <NavLink to="/clusters" className={"current-breadcrumb-link"}>Clusters</NavLink>
                    <PeriodBreadcrumb/>
                </Breadcrumb>
                <HeaderActions toggleMenu={props.toggleMenu} refresh={refresh}/>
            </div>

            <div className="loader">
                <ConditionalRender if={loading === DATA_LOADING}>
                    <div className="bar"/>
                </ConditionalRender>
            </div>

            {/* Metric Widgets */}
            <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4">
                <WidgetCard
                    name="Clusters"
                    iconClassName="processor"
                    icon="fa-circle-nodes"
                    value={clusterCount}
                    cardTooltipText="The total number of monitored clusters"
                    loading={loading === DATA_LOADING}
                />
                <WidgetCard
                    name="Availability Groups"
                    iconClassName="memory"
                    icon="fa-layer-group"
                    value={groupSummary}
                    cardTooltipText="Availability groups with a HEALTHY synchronization state"
                    loading={loading === DATA_LOADING}
                />
                <WidgetCard
                    name="Replicas"
                    iconClassName="io"
                    icon="fa-clone"
                    value={replicaSummary}
                    cardTooltipText="Availability replicas with a HEALTHY synchronization state"
                    loading={loading === DATA_LOADING}
                />
                <WidgetCard
                    name="Alerts"
                    iconClassName="changes"
                    icon="fa-exclamation-triangle"
                    value={alertCount}
                    cardTooltipText="Active availability group alerts across all clusters"
                    loading={loading === DATA_LOADING}
                />
            </div>

            {/* Clusters */}
            <div className="row row-cols-1">
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-circle-nodes fa-fw" aria-hidden="true"/>
                            Monitored clusters
                            <span className="badge bg-info"
                                  data-tooltip-id="global-tooltip"
                                  data-tooltip-content="Total cluster count">{clustersDataTable.length}</span>
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                               data-bs-target="#collapseClusters" aria-expanded="false"
                               aria-controls="collapseClusters"/>
                        </div>
                        <div id="collapseClusters" className="card-body collapse show">
                            <ClustersTable loading={loading} clustersDataTable={clustersDataTable}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;
